import type { Theme, SxProps } from '@mui/material/styles';

import { usePopover } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import ListSubheader from '@mui/material/ListSubheader';

import { Iconify } from '../iconify';
import { Scrollbar } from '../scrollbar';
import { CustomPopover } from '../custom-popover';

import type { IconifyName } from '../iconify';

// ----------------------------------------------------------------------

const ICON_BOX: SxProps<Theme> = {
  width: 24,
  height: 24,
  flexShrink: 0,
  borderRadius: '50%',
  color: 'primary.main',
  bgcolor: 'primary.lighter',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export type HeaderSwitcherProps<T> = {
  /** Registered Iconify icon name shown in the indicator + each menu item. */
  icon: IconifyName;
  items: T[];
  activeItem: T | null;
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  /** Optional second line rendered under the label in the dropdown. */
  getSubtitle?: (item: T) => string;
  /**
   * Optional override for the label shown on the trigger button / static chip
   * (the dropdown items still use `getLabel`). Lets a combined switcher read e.g.
   * "School · Year" on the trigger while listing plain year names in the menu.
   */
  getActiveLabel?: (item: T) => string;
  /**
   * Optional grouping key. When provided AND there is more than one distinct
   * group, menu items render under a `ListSubheader` per group (in first-seen
   * order). A single group renders flat (no redundant header).
   */
  getGroup?: (item: T) => string;
  onSelect: (id: string) => void;
  emptyText: string;
  isLoading: boolean;
  /** When true, the picker (when there's more than one item) is non-interactive. */
  disabled?: boolean;
  /** Tooltip shown over the disabled picker explaining why it can't be changed. */
  disabledTooltip?: string;
};

// ----------------------------------------------------------------------

/**
 * Always-visible header indicator + picker. Renders a skeleton while loading,
 * `emptyText` when there's nothing, a static label when there's exactly one
 * item, and a popover picker when there's more than one. Shared by the school
 * and class switchers so the two scopes look and behave identically.
 */
export function HeaderSwitcher<T>({
  icon,
  items,
  activeItem,
  getId,
  getLabel,
  getSubtitle,
  getActiveLabel,
  getGroup,
  onSelect,
  emptyText,
  isLoading,
  disabled = false,
  disabledTooltip,
}: HeaderSwitcherProps<T>) {
  const { open, anchorEl, onClose, onOpen } = usePopover();

  if (isLoading) {
    return <Skeleton variant="rounded" width={160} height={28} />;
  }

  if (!activeItem) {
    return (
      <Typography variant="body2" sx={{ color: 'text.disabled' }}>
        {emptyText}
      </Typography>
    );
  }

  const iconEl = (
    <Box sx={ICON_BOX}>
      <Iconify width={16} icon={icon} />
    </Box>
  );

  const label = (
    <Box
      component="span"
      sx={{ typography: 'subtitle2', display: { xs: 'none', sm: 'inline-flex' } }}
    >
      {(getActiveLabel ?? getLabel)(activeItem)}
    </Box>
  );

  // Single item — static, non-interactive indicator (still always visible).
  if (items.length <= 1) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        {iconEl}
        {label}
      </Box>
    );
  }

  const button = (
    <ButtonBase
      disableRipple
      disabled={disabled}
      onClick={disabled ? undefined : onOpen}
      sx={{
        py: 0.5,
        gap: 1,
        borderRadius: 1,
        ...(open && { bgcolor: 'action.hover' }),
      }}
    >
      {iconEl}
      {label}
      <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }} />
    </ButtonBase>
  );

  const renderItem = (item: T) => {
    const subtitle = getSubtitle?.(item);
    return (
      <MenuItem
        key={getId(item)}
        selected={getId(item) === getId(activeItem)}
        onClick={() => {
          onSelect(getId(item));
          onClose();
        }}
        sx={{ height: 56, gap: 1.5 }}
      >
        <Box sx={ICON_BOX}>
          <Iconify width={16} icon={icon} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography noWrap variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
            {getLabel(item)}
          </Typography>
          {subtitle && (
            <Typography noWrap variant="caption" sx={{ color: 'text.secondary' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </MenuItem>
    );
  };

  // Flat list, or — when `getGroup` yields more than one distinct group — grouped
  // under a `ListSubheader` per group (in first-seen order). A single group stays flat.
  const renderMenuItems = () => {
    if (!getGroup) return items.map(renderItem);
    const order: string[] = [];
    const byGroup = new Map<string, T[]>();
    for (const item of items) {
      const g = getGroup(item);
      if (!byGroup.has(g)) {
        byGroup.set(g, []);
        order.push(g);
      }
      byGroup.get(g)!.push(item);
    }
    if (order.length <= 1) return items.map(renderItem);
    return order.map((g) => [
      <ListSubheader key={`group-${g}`} disableSticky sx={{ bgcolor: 'transparent', lineHeight: '32px' }}>
        {g}
      </ListSubheader>,
      ...byGroup.get(g)!.map(renderItem),
    ]);
  };

  return (
    <>
      {disabled && disabledTooltip ? (
        // Tooltip needs a non-disabled wrapper element to receive hover events,
        // since MUI suppresses them on the disabled ButtonBase itself.
        <Tooltip title={disabledTooltip}>
          <span style={{ display: 'inline-flex' }}>{button}</span>
        </Tooltip>
      ) : (
        button
      )}

      <CustomPopover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        slotProps={{
          arrow: { placement: 'top-left' },
          paper: { sx: { mt: 0.5, ml: -1.55, width: 280 } },
        }}
      >
        <Scrollbar sx={{ maxHeight: 320 }}>
          <MenuList>{renderMenuItems()}</MenuList>
        </Scrollbar>
      </CustomPopover>
    </>
  );
}
